/**
 * The Friedman test for k related samples (a one-way repeated-measures design on ranks),
 * with Kendall's W and the Nemenyi and Conover post hoc tests.
 *
 * Within each subject (row) the k scores are ranked, ties taking their average rank.
 * With R_j the sum of condition j's ranks over the n subjects,
 *
 *   χ²_F = 12 / (n k (k + 1)) · Σ R_j² − 3 n (k + 1)
 *
 * and, because ties shrink the spread of ranks, the tie-corrected statistic divides that by
 *
 *   C = 1 − Σ(t³ − t) / (n k (k² − 1)),   summed over every set of tied scores in every row.
 *
 * This is exactly R's `friedman.test` and `scipy.stats.friedmanchisquare`. It is referred to
 * χ² with k − 1 degrees of freedom; the approximation is dependable from about n ≥ 10 for
 * k = 3 (exact tables exist for smaller designs).
 *
 * Kendall's coefficient of concordance W = χ²_F / (n (k − 1)) runs from 0 (no agreement
 * between subjects' orderings) to 1 (every subject orders the conditions identically).
 *
 * Post hoc:
 * - Nemenyi (1963): q = |R_i − R_j| / √(n k (k + 1) / 12), referred to the studentized
 *   range with k means and infinite df — the rank-based analogue of Tukey's HSD
 *   (Demšar 2006). Not tie-corrected, as in `PMCMRplus::frdAllPairsNemenyiTest`.
 * - Conover (1999, p. 371): t = |R_i − R_j| / √(2 (n A − Σ R_j²) / ((n − 1)(k − 1))) on
 *   (n − 1)(k − 1) df, where A = Σ r² over all ranks. Holm-adjusted p-values are given
 *   alongside the unadjusted ones.
 */

import { averageRanks, median, tieGroupSizes } from "@/lib/stats/descriptive";
import { chiSquareUpper, studentTTwoTailed } from "@/lib/stats/distributions";
import { holmAdjust } from "@/lib/tool-kit/stats-tests-more/adjust";
import { ptukeyUpper } from "@/lib/tool-kit/stats-tests-more/studentized-range";

export interface FriedmanPair {
  first: number;
  second: number;
  /** R_first − R_second. */
  rankSumDifference: number;
  /** Nemenyi: the studentized range statistic and its p-value. */
  q: number;
  pNemenyi: number;
  /** Conover: t, its two-sided p-value, and the Holm-adjusted p. */
  t: number;
  pConover: number;
  pConoverHolm: number;
}

export interface FriedmanResult {
  n: number;
  k: number;
  /** Ranks within each subject, n rows × k columns. */
  ranks: number[][];
  rankSums: number[];
  meanRanks: number[];
  medians: number[];
  /** Σ(t³ − t) over every row. */
  tieTerm: number;
  tieCorrection: number;
  /** χ²_F before the tie correction. */
  chiSquareUncorrected: number;
  chiSquare: number;
  df: number;
  p: number;
  kendallW: number;
  conoverDf: number;
  pairs: FriedmanPair[];
}

export type FriedmanFailure =
  | "too-few-subjects"
  | "too-few-conditions"
  | "all-tied";

export type FriedmanOutcome =
  | { ok: true; result: FriedmanResult }
  | { ok: false; reason: FriedmanFailure };

function column(rows: readonly (readonly number[])[], j: number): number[] {
  return rows.map((row) => row[j]);
}

function pairwise(
  rankSums: number[],
  ranks: number[][],
  n: number,
  k: number
): { pairs: FriedmanPair[]; df: number } {
  const nemenyiScale = Math.sqrt((n * k * (k + 1)) / 12);
  const squares = ranks.reduce(
    (acc, row) => acc + row.reduce((a, r) => a + r * r, 0),
    0
  );
  const sumOfSquaredSums = rankSums.reduce((acc, r) => acc + r * r, 0);
  const df = (n - 1) * (k - 1);
  const conoverScale = Math.sqrt((2 * (n * squares - sumOfSquaredSums)) / df);
  const pairs: FriedmanPair[] = [];
  for (let i = 0; i < k; i++) {
    for (let j = i + 1; j < k; j++) {
      const rankSumDifference = rankSums[i] - rankSums[j];
      const gap = Math.abs(rankSumDifference);
      const q = gap / nemenyiScale;
      const t =
        conoverScale > 0 ? gap / conoverScale : Number.POSITIVE_INFINITY;
      pairs.push({
        first: i,
        second: j,
        rankSumDifference,
        q,
        pNemenyi: Math.min(1, ptukeyUpper(q, k, Number.POSITIVE_INFINITY)),
        t,
        pConover: Number.isFinite(t) ? studentTTwoTailed(t, df) : 0,
        pConoverHolm: 0,
      });
    }
  }
  const holm = holmAdjust(pairs.map((pair) => pair.pConover));
  for (const [index, pair] of pairs.entries()) {
    pair.pConoverHolm = holm[index];
  }
  return { pairs, df };
}

/** `rows` has one array per subject and one entry per condition. */
export function friedman(
  rows: readonly (readonly number[])[]
): FriedmanOutcome {
  const n = rows.length;
  const k = rows[0]?.length ?? 0;
  if (n < 2) {
    return { ok: false, reason: "too-few-subjects" };
  }
  if (k < 2) {
    return { ok: false, reason: "too-few-conditions" };
  }
  const ranks = rows.map((row) => averageRanks(row));
  let tieTerm = 0;
  for (const row of rows) {
    tieTerm += tieGroupSizes(row).reduce((acc, t) => acc + t ** 3 - t, 0);
  }
  const tieCorrection = 1 - tieTerm / (n * k * (k * k - 1));
  if (tieCorrection <= 0) {
    return { ok: false, reason: "all-tied" };
  }
  const rankSums = Array.from({ length: k }, (_, j) =>
    ranks.reduce((acc, row) => acc + row[j], 0)
  );
  const chiSquareUncorrected = Math.max(
    0,
    (12 / (n * k * (k + 1))) * rankSums.reduce((acc, r) => acc + r * r, 0) -
      3 * n * (k + 1)
  );
  const chiSquare = chiSquareUncorrected / tieCorrection;
  const df = k - 1;
  const { pairs, df: conoverDf } = pairwise(rankSums, ranks, n, k);
  return {
    ok: true,
    result: {
      n,
      k,
      ranks,
      rankSums,
      meanRanks: rankSums.map((r) => r / n),
      medians: Array.from({ length: k }, (_, j) => median(column(rows, j))),
      tieTerm,
      tieCorrection,
      chiSquareUncorrected,
      chiSquare,
      df,
      p: chiSquareUpper(chiSquare, df),
      kendallW: chiSquare / (n * df),
      conoverDf,
      pairs,
    },
  };
}
